/**
 * Live password checklist: ticks each rule off as the user types, so they
 * know what's missing before they press the button. Mirrors the server rules
 * (utils/passwordRules).
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import {
  CheckCircle as MetIcon,
  RadioButtonUnchecked as UnmetIcon,
} from "@mui/icons-material";
import { getPasswordChecklist } from "@/utils/passwordRules";

interface PasswordRequirementsProps {
  password: string;
  /** id for aria-describedby on the password field. */
  id?: string;
}

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  id = "password-requirements",
}) => {
  const checks = getPasswordChecklist(password ?? "");
  return (
    <Box
      id={id}
      component="ul"
      aria-label="Password requirements"
      data-testid="password-requirements"
      sx={{ listStyle: "none", m: 0, mt: 0.5, mb: 1, p: 0 }}
    >
      {checks.map((check) => (
        <Box
          component="li"
          key={check.id}
          data-met={check.met ? "true" : "false"}
          sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.25 }}
        >
          {check.met ? (
            <MetIcon sx={{ fontSize: 16, color: "success.main" }} aria-hidden />
          ) : (
            <UnmetIcon sx={{ fontSize: 16, color: "text.disabled" }} aria-hidden />
          )}
          <Typography
            variant="caption"
            sx={{ color: check.met ? "success.main" : "text.secondary" }}
          >
            {check.label}
            <Box component="span" sx={visuallyHidden}>
              {check.met ? " (done)" : " (missing)"}
            </Box>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Strings: in sx a bare 1 is 100% (width) and 8px (margin).
const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;

export default PasswordRequirements;
